import React from "react";
import { Mail, Scale, Clock } from "lucide-react";

const TermsOfService = () => {
  const lastUpdated = "March 2026";

  const sections = [
    {
      id: "acceptance",
      title: "Acceptance of Terms",
      content:
        "These Terms of Service constitute a legally binding agreement between you and Synthera Security. By accessing our website, engaging our services, or communicating with our team, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you are acting on behalf of an organization, you represent that you have the authority to bind that entity.",
    },
    {
      id: "services",
      title: "Scope of Services",
      content:
        "Synthera Security provides enterprise cybersecurity consulting, AI security governance, and data analytics services. All services are delivered based on agreed project scope, documentation, and contractual terms. We reserve the right to modify or update service offerings without prior notice.",
    },
    {
      id: "use",
      title: "Permitted Use",
      content:
        "You agree to use our website and services only for lawful purposes. You must not attempt to disrupt, compromise, or gain unauthorized access to any systems, networks, or data associated with Synthera Security.",
    },
    {
      id: "confidentiality",
      title: "Confidentiality",
      content:
        "Both parties agree to maintain the confidentiality of any non-public, proprietary, or sensitive information shared during the course of engagement. This includes technical data, business information, and security-related findings.",
    },
    {
      id: "disclaimer",
      title: "Disclaimer of Warranties",
      content:
        "All services and materials are provided on an 'as is' and 'as available' basis. Synthera Security makes no warranties, express or implied, including but not limited to fitness for a particular purpose or uninterrupted service availability.",
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      content:
        "To the fullest extent permitted by law, Synthera Security shall not be liable for any indirect, incidental, or consequential damages, including loss of data, revenue, or business interruption arising from the use of our services.",
    },
    {
      id: "third-party",
      title: "Third-Party Services",
      content:
        "Our services may integrate or rely on third-party tools, platforms, or infrastructure. Synthera Security is not responsible for the performance, availability, or security practices of third-party providers.",
    },
    {
      id: "law",
      title: "Governing Law",
      content:
        "These Terms shall be governed by and interpreted in accordance with applicable laws. Any disputes arising in connection with these Terms shall be subject to the jurisdiction of the competent courts.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pt-24">
      {/* Clean Header (reduced top spacing) */}
      <header className="bg-white border-b border-slate-200 py-12 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold mb-4">
            <Scale className="w-4 h-4" />
            Legal
          </div>

          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Terms of Service
          </h1>

          <p className="text-slate-500 text-sm flex justify-center gap-2">
            <Clock className="w-4 h-4" />
            Last Updated: {lastUpdated}
          </p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-sm">
          {/* Intro Box */}
          <div className="mb-10 p-5 rounded-xl bg-indigo-600 text-white">
            <h2 className="font-semibold mb-2">Agreement Notice</h2>
            <p className="text-sm text-indigo-100">
              By accessing Synthera Security services, you agree to comply with
              these terms.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {sections.map((section, idx) => (
              <section key={section.id} id={section.id}>
                <h2 className="text-xl font-bold mb-3">
                  {idx + 1}. {section.title}
                </h2>
                <p className="text-slate-600 leading-7">{section.content}</p>
              </section>
            ))}
          </div>
        </div>

        {/* Bottom Contact (clean + minimal) */}
        <div className="mt-12 text-center">
          <h3 className="font-semibold text-lg mb-2">Need Help?</h3>
          <p className="text-slate-500 text-sm mb-4">
            Contact our team for any legal or compliance questions.
          </p>

          <a
            href="mailto:customersupport@synthera.com"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg text-sm font-medium"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>
        </div>

        {/* Footer */}
        <footer className="mt-10 text-center text-slate-400 text-xs">
          © 2026 Synthera Security. All rights reserved.
        </footer>
      </div>
    </div>
  );
};

export default TermsOfService;
